<template>
  <div class="co-date-picker">
    <div class="co-date-picker-input">
      <v-text-field
        :value="textFieldValue"
        :label="title"
        :hide-details="hideDetails"
        :rules="rules"
        outlined
        readonly
        :clearable="clearable"
        @click:clear="inputDate = null"
        class="cursor-pointer"
      ></v-text-field>
    </div>
    <v-chip-group
      v-if="dateChips.length > 0"
      v-model="selectedDateChip"
      :value-comparator="dateChipComparator"
      class="common-dates-chip-group"
      column
      color="secondary"
    >
      <v-chip
        v-for="dateChip in dateChips"
        :key="dateChip.date.format('YYYYMMDD')"
        :value="dateChip"
        outlined
      >
        {{ dateChip.text }}
      </v-chip>
    </v-chip-group>

    <v-menu
      v-model="showCalendarMenu"
      :activator="activator"
      :close-on-content-click="false"
      :nudge-right="40"
      :left="left"
      transition="scale-transition"
      offset-y
      min-width="598"
    >
      <div>
        <v-card flat class="mx-auto co-date-picker-menu-card">
          <v-card-title v-show="!!promptTitle">{{ promptTitle }}</v-card-title>
          <v-app-bar
            flat
            color="rgba(0, 0, 0, 0.03)"
          >
            <v-btn
              icon
              @click="prev"
              class="p-absolute l-0 ml-2"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <v-toolbar-title class="title flex-1 text-center">
              <span>{{ firstCalendarTitle }}</span>
            </v-toolbar-title>

            <v-toolbar-title class="title flex-1 text-center">
              <span>{{ secondCalendarTitle }}</span>
            </v-toolbar-title>

            <v-btn
              icon
              @click="next"
              class="p-absolute r-0 mr-2"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-app-bar>
          <v-row class="ma-0">
            <v-col cols="6" class="pa-0">
              <v-calendar
                ref="firstCalendar"
                show-week
                locale-first-day-of-year="4"
                v-model="focusFirstCalendar"
                :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                type="month"
                color="primary"
                @click:day="calendarClickedDate"
                :show-month-on-first="false"
              >
                <template v-slot:day-label="{ day, date, present }">
                  <template v-if="isSelectedDate(date)">
                    <v-btn
                      fab
                      elevation="0"
                      color="primary"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                  <template v-else-if="isDisabledDate(date)">
                    <div class="disabled-date">
                      {{ day }}
                    </div>
                  </template>
                  <template v-else-if="present">
                    <v-btn
                      fab
                      elevation="0"
                      color="bg-primary-lighten-55"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      fab
                      elevation="0"
                      color="transparent"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                </template>
              </v-calendar>
            </v-col>
            <v-col cols="6" class="pa-0">
              <v-calendar
                ref="secondCalendar"
                show-week
                locale-first-day-of-year="4"
                v-model="focusSecondCalendar"
                :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                type="month"
                color="primary"
                @click:day="calendarClickedDate"
                :show-month-on-first="false"
              >
                <template v-slot:day-label="{ day, date, present }">
                  <template v-if="isSelectedDate(date)">
                    <v-btn
                      fab
                      elevation="0"
                      color="primary"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                  <template v-else-if="isDisabledDate(date)">
                    <div class="disabled-date">
                      {{ day }}
                    </div>
                  </template>
                  <template v-else-if="present">
                    <v-btn
                      fab
                      elevation="0"
                      color="bg-primary-lighten-55"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      fab
                      elevation="0"
                      color="transparent"
                      width="32"
                      height="32"
                    >
                      {{ day }}
                    </v-btn>
                  </template>
                </template>
              </v-calendar>
            </v-col>
          </v-row>
        </v-card>
      </div>
    </v-menu>
  </div>
</template>

<script>

export default {
  name: 'DatePicker',
  props: {
    value: Object,
    title: String,
    promptTitle: String,
    clearable: {
      type: Boolean,
      default: false
    },
    lastSelectableDate: Object,
    firstSelectableDate: Object,
    alwaysShowYear: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: [Boolean, String],
      default: true
    },
    rules: {
      type: Array,
      default: () => []
    },
    dateChips: {
      type: Array, // array med objekt med attributen text, date
      default: () => []
    },
    left: Boolean
  },
  data: () => ({
    selectedDateChip: null,
    showCalendarMenu: false,
    activator: null,
    firstCalendarDate: window.dayjs.today().startOf('month'),
    secondCalendarDate: window.dayjs.today().startOf('month').add(1, 'month')
  }),
  computed: {
    inputDate: {
      get: function () {
        return this.value
      },
      set: function (val) {
        this.$emit('input', val)
      }
    },
    focusFirstCalendar: {
      get: function () {
        return this.firstCalendarDate.format('YYYY-MM-DD')
      },
      set: function (val) {
        this.firstCalendarDate = window.dayjs.parseDate(val)
      }
    },
    focusSecondCalendar: {
      get: function () {
        return this.secondCalendarDate.format('YYYY-MM-DD')
      },
      set: function (val) {
        this.secondCalendarDate = window.dayjs.parseDate(val)
      }
    },
    firstCalendarTitle: function () {
      return this.firstCalendarDate.format('MMMM YYYY')
    },
    secondCalendarTitle: function () {
      return this.secondCalendarDate.format('MMMM YYYY')
    },
    textFieldValue: function () {
      if (this.inputDate === null || this.inputDate === undefined) {
        return ''
      }
      if (!this.alwaysShowYear && this.inputDate.isSame(window.dayjs.today(), 'year')) {
        return this.inputDate.format('ddd D MMM')
      }
      return this.inputDate.format('ddd D MMM YYYY')
    }
  },
  methods: {
    isSelectedDate: function (dt) {
      return window.dayjs.parseDate(dt).isSame(this.inputDate, 'date')
    },
    prev: function () {
      this.$refs.firstCalendar.prev()
      this.$refs.secondCalendar.prev()
    },
    next: function () {
      this.$refs.firstCalendar.next()
      this.$refs.secondCalendar.next()
    },
    calendarClickedDate: function (e) {
      let date = e.date
      if (this.isDisabledDate(date)) {
        return false
      }
      date = window.dayjs.parseDate(e.date)
      this.inputDate = date
      this.showCalendarMenu = false
    },
    isDisabledDate: function (date) {
      const dt = window.dayjs.parseDate(date)
      let isDisabled = this.lastSelectableDate && dt.isAfter(this.lastSelectableDate)
      isDisabled = isDisabled || (this.firstSelectableDate && dt.isBefore(this.firstSelectableDate))
      return isDisabled
    },
    dateChipComparator: function (a, dateChip) {
      return dateChip && dateChip.date.isSame(this.inputDate, 'date')
    }
  },
  watch: {
    selectedDateChip: function () {
      if (this.selectedDateChip) {
        this.inputDate = this.selectedDateChip.date.clone()
      }
    },
    inputDate: function () {
      const dateChip = this.dateChips.find(dc => dc.date.isSame(this.inputDate, 'date'))
      if (dateChip) {
        this.selectedDateChip = dateChip
      } else {
        this.selectedDateChip = null
      }
    }
  },
  mounted () {
    this.activator = this.$el.querySelector('.co-date-picker-input')
  }
}
</script>
